import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCell,
  MatCell,
  MatHeaderRow,
  MatRow,
  MatHeaderCellDef,
  MatCellDef,
  MatHeaderRowDef,
  MatRowDef,
} from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard } from '@angular/material/card';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, startWith } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ProjectService } from '../services/project.service';
import { AccountService } from '../account/account.service';
import { TenantUserService } from '../services/tenant-user.service';
import { Project, ProjectStatus } from '../models/project.model';
import { Membership } from '../models/membership.model';
import { USER_ROLE } from '../models/user-role.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Modal } from '@spirit-testing/component-library';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { AvatarComponent } from '../components/avatar/avatar.component';
import { AvatarGroupComponent } from '../components/avatar-group/avatar-group.component';
import { PageHeaderComponent } from '../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../components/stat-group/stat-group.component';
import { SearchBarComponent } from '../components/search-bar/search-bar.component';
import { StatusPillComponent, StatusKind } from '../components/status-pill/status-pill.component';
import { OverviewPaginationComponent } from '../components/overview-pagination/overview-pagination.component';
import { PageSizePreferenceService } from '../services/page-size-preference.service';
import { ScrollContainerDirective } from '../shared/scroll-container/scroll-container.directive';
import { getProjectIconDef } from '../utils/project-icon.utils';
import { canManageProject } from '../utils/project-permission.utils';

const DEFAULT_PROJECT_PAGE_SIZE = 10;
const PROJECT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const PROJECT_PAGE_SIZE_ROUTE_KEY = 'dashboard-projects';

@Component({
  selector: 'app-dashboard',
  imports: [
    TranslatePipe,
    DatePipe,
    ReactiveFormsModule,
    MatButton,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatProgressSpinner,
    MatCard,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSort,
    MatSortHeader,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    Modal,
    ConfirmDialogComponent,
    AvatarComponent,
    AvatarGroupComponent,
    PageHeaderComponent,
    StatGroupComponent,
    SearchBarComponent,
    StatusPillComponent,
    OverviewPaginationComponent,
    ScrollContainerDirective,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  private projectService = inject(ProjectService);
  private accountService = inject(AccountService);
  private tenantUserService = inject(TenantUserService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private pageSizePref = inject(PageSizePreferenceService);

  readonly projectTableColumns = ['name', 'responsible', 'status', 'progress', 'actions'];
  readonly tenantUserMap = signal<Map<string, string>>(new Map());

  readonly userRole = signal<string | null>(null);
  readonly memberships = signal<Membership[]>([]);
  readonly isPlatformAdmin = computed(() => this.userRole() === USER_ROLE.PLATFORM_ADMIN);
  readonly projectRoleMap = computed(() => {
    const map = new Map<string, Membership['role']>();
    for (const m of this.memberships()) map.set(m.projectId, m.role);
    return map;
  });

  canManageProject(project: Project): boolean {
    return canManageProject(this.userRole(), this.projectRoleMap().get(project.id) ?? null);
  }

  /**
   * Label-Key für die „Verwalten"-Spalte bei Nicht-Admin-Mitgliedern.
   * Reader (PROJECT_READER) sehen „Nur Lesen", Writer sehen „Mitarbeit" (NEX-498).
   */
  memberLabelKey(project: Project): string {
    return this.projectRoleMap().get(project.id) === 'PROJECT_READER'
      ? 'dashboard.projects.readOnly'
      : 'dashboard.projects.memberOnly';
  }

  readonly projectsLoading = signal(true);
  readonly loadError = signal(false);
  readonly projects = signal<Project[]>([]);
  readonly projectSearchTerm = signal('');
  readonly statusFilter = signal<ProjectStatus | null>(null);
  readonly leadFilter = signal<string | null>(null);
  readonly sortField = signal<string>('');
  readonly sortDirection = signal<'asc' | 'desc' | ''>('');

  readonly zeitraumForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  readonly zeitraumStart = toSignal(this.zeitraumForm.controls.start.valueChanges.pipe(startWith(null)), {
    initialValue: null,
  });
  readonly zeitraumEnd = toSignal(this.zeitraumForm.controls.end.valueChanges.pipe(startWith(null)), {
    initialValue: null,
  });

  readonly responsibleUsers = computed(() => {
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    for (const project of this.projects()) {
      for (const id of this.projectLeadIds(project)) {
        if (seen.has(id)) continue;
        seen.add(id);
        result.push({ id, name: this.getLeadName(id) });
      }
    }
    return result;
  });

  readonly activeProjectCount = computed(() => {
    const today = new Date().toISOString().split('T')[0];
    return this.projects().filter((p) => !p.endDate || p.endDate >= today).length;
  });

  private readonly langChange = toSignal(this.translate.onLangChange.pipe(startWith(null)), {
    initialValue: null,
  });

  readonly projectStats = computed((): StatItem[] => {
    this.langChange();
    return [
      { label: this.translate.instant('dashboard.stats.total'), value: this.projects().length },
      {
        label: this.translate.instant('dashboard.stats.active'),
        value: this.activeProjectCount(),
        color: 'var(--color-status-aktiv-fg)',
      },
      { label: this.translate.instant('dashboard.stats.testCases'), value: '—' },
    ];
  });

  readonly filteredProjects = computed(() => {
    const term = this.projectSearchTerm().toLowerCase().trim();
    const statusF = this.statusFilter();
    const responsibleF = this.leadFilter();
    const zStart = this.zeitraumStart();
    const zEnd = this.zeitraumEnd();
    const field = this.sortField();
    const dir = this.sortDirection();

    let result = this.projects();

    if (term) {
      result = result.filter((p) => p.name.toLowerCase().includes(term));
    }
    if (statusF) {
      result = result.filter((p) => p.status === statusF);
    }
    if (responsibleF) {
      result = result.filter((p) => this.projectLeadIds(p).includes(responsibleF));
    }
    if (zStart || zEnd) {
      const startStr = zStart ? this.toDateStr(zStart) : null;
      const endStr = zEnd ? this.toDateStr(zEnd) : null;
      result = result.filter((p) => {
        const pEnd = p.endDate ?? '9999-12-31';
        const afterStart = !startStr || pEnd >= startStr;
        const beforeEnd = !endStr || p.startDate <= endStr;
        return afterStart && beforeEnd;
      });
    }
    if (field && dir) {
      result = [...result].sort((a, b) => {
        let aVal = '';
        let bVal = '';
        switch (field) {
          case 'name':
            aVal = a.name;
            bVal = b.name;
            break;
          case 'startDate':
            aVal = a.startDate;
            bVal = b.startDate;
            break;
          case 'responsible':
            aVal = this.leadNamesSortKey(a);
            bVal = this.leadNamesSortKey(b);
            break;
          case 'status':
            aVal = this.getProjectStatus(a);
            bVal = this.getProjectStatus(b);
            break;
        }
        const cmp = aVal.localeCompare(bVal);
        return dir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  });

  readonly projectPageIndex = signal(0);
  readonly projectPageSize = signal(
    this.pageSizePref.get(PROJECT_PAGE_SIZE_ROUTE_KEY, PROJECT_PAGE_SIZE_OPTIONS, DEFAULT_PROJECT_PAGE_SIZE),
  );
  readonly projectPageSizeOptions = PROJECT_PAGE_SIZE_OPTIONS;

  readonly clampedPageIndex = computed(() => {
    const total = this.filteredProjects().length;
    const size = this.projectPageSize();
    if (total === 0) return 0;
    return Math.min(this.projectPageIndex(), Math.ceil(total / size) - 1);
  });

  readonly pagedProjects = computed(() => {
    const start = this.clampedPageIndex() * this.projectPageSize();
    return this.filteredProjects().slice(start, start + this.projectPageSize());
  });

  readonly showDeleteProject = signal(false);
  readonly projectToDelete = signal<Project | null>(null);
  readonly deleteProjectBusy = signal(false);
  readonly deleteProjectMessage = computed(() => {
    const p = this.projectToDelete();
    if (!p) return '';
    return this.translate.instant('dashboard.deleteProject.confirmation', {
      name: p.name,
      testCases: p.testCaseCount ?? 0,
      executions: p.runningExecutionCount ?? 0,
    });
  });

  readonly isZeitraumActive = computed(() => this.zeitraumStart() !== null || this.zeitraumEnd() !== null);

  constructor() {
    this.loadData();
  }

  loadData(): void {
    this.projectsLoading.set(true);
    this.loadError.set(false);
    this.accountService
      .getProfile()
      .pipe(
        tap((profile) => this.userRole.set(profile.role)),
        switchMap((profile) =>
          forkJoin({
            // NEX-725: `list()` verwendet den `GET /api/v1/projects`-Endpoint, der fuer
            // PLATFORM_ADMIN/TENANT_ADMIN alle Tenant-Projekte liefert (ohne Membership).
            // Fuer Nicht-Admins geht das BE denselben Membership-Pfad wie `listForCurrentUser`.
            projects: this.projectService.list(),
            users: this.tenantUserService.getUsersDirectory(profile.customerId),
            memberships: this.accountService.getMemberships(),
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ projects, users, memberships }) => {
          this.projects.set(projects);
          this.memberships.set(memberships);
          const userMap = new Map<string, string>();
          users.forEach((u) => userMap.set(u.id, u.displayName));
          this.tenantUserMap.set(userMap);
          this.projectsLoading.set(false);
        },
        error: () => {
          this.projects.set([]);
          this.loadError.set(true);
          this.projectsLoading.set(false);
        },
      });
  }

  getLeadName(userId: string | null | undefined): string {
    if (!userId) return '-';
    return this.tenantUserMap().get(userId) ?? '-';
  }

  projectLeadIds(project: Project): string[] {
    if (project.leadUserIds && project.leadUserIds.length > 0) return project.leadUserIds;
    return project.leadUserId ? [project.leadUserId] : [];
  }

  projectLeadNames(project: Project): string[] {
    // Unaufgelöste IDs ('-' aus getLeadName) bleiben enthalten, damit ein Projekt
    // mit Lead nicht fälschlich als „—" erscheint, solange tenantUserMap noch
    // lädt oder ein Lead nicht (mehr) im Tenant ist (NEX-447 Review).
    return this.projectLeadIds(project).map((id) => this.getLeadName(id));
  }

  private leadNamesSortKey(project: Project): string {
    return this.projectLeadNames(project).slice().sort().join(', ');
  }

  getProjectStatus(project: Project): StatusKind {
    const map: Record<ProjectStatus, StatusKind> = {
      entwurf: 'inaktiv',
      aktiv: 'aktiv',
      pausiert: 'pending',
      archiviert: 'inaktiv',
    };
    return (project.status ? map[project.status] : undefined) ?? 'inaktiv';
  }

  getStatusLabel(project: Project): string | null {
    if (!project.status) return null;
    return this.translate.instant('dashboard.filter.status_' + project.status);
  }

  getProjectProgress(project: Project): number {
    return project.progress ?? 0;
  }

  onSortChange(sort: Sort): void {
    this.sortField.set(sort.active);
    this.sortDirection.set(sort.direction);
    this.projectPageIndex.set(0);
  }

  onProjectSearchChange(term: string): void {
    this.projectSearchTerm.set(term);
    this.projectPageIndex.set(0);
  }

  setStatusFilter(status: ProjectStatus | null): void {
    this.statusFilter.set(status);
    this.projectPageIndex.set(0);
  }

  setResponsibleFilter(userId: string | null): void {
    this.leadFilter.set(userId);
    this.projectPageIndex.set(0);
  }

  clearZeitraumFilter(): void {
    this.zeitraumForm.reset();
    this.projectPageIndex.set(0);
  }

  onNavigateToCreate(): void {
    this.router.navigate(['/projects', 'new']);
  }

  onNavigateToEdit(project: Project): void {
    this.router.navigate(['/projects', project.id, 'edit']);
  }

  onNavigateToProject(project: Project): void {
    this.router.navigate(['/projects', project.id, 'requirements']);
  }

  onRowKeydown(event: Event, project: Project): void {
    if (event.target !== event.currentTarget) return;
    if ((event as KeyboardEvent).key === ' ') event.preventDefault();
    this.onNavigateToProject(project);
  }

  onOpenDeleteModal(project: Project): void {
    this.projectToDelete.set(project);
    this.showDeleteProject.set(true);
  }

  onConfirmDelete(): void {
    const project = this.projectToDelete();
    if (!project) return;
    this.deleteProjectBusy.set(true);
    this.projectService
      .delete(project.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.projects.update((list) => list.filter((p) => p.id !== project.id));
          this.deleteProjectBusy.set(false);
          this.showDeleteProject.set(false);
          this.projectToDelete.set(null);
        },
        error: (err: HttpErrorResponse) => {
          this.deleteProjectBusy.set(false);
          const key =
            err.status === 403
              ? 'dashboard.forbiddenError'
              : err.status === 409
                ? 'dashboard.deleteProjectConflict'
                : 'dashboard.deleteProjectError';
          this.snackBar.open(this.translate.instant(key), undefined, { duration: 4000 });
        },
      });
  }

  onCancelDelete(): void {
    this.showDeleteProject.set(false);
    this.projectToDelete.set(null);
    this.deleteProjectBusy.set(false);
  }

  onPageSizeSelectChange(size: number): void {
    this.projectPageSize.set(size);
    this.projectPageIndex.set(0);
    this.pageSizePref.set(PROJECT_PAGE_SIZE_ROUTE_KEY, size, PROJECT_PAGE_SIZE_OPTIONS);
  }

  getProjectIconDef(project: Project): { matIcon: string; color: string } {
    return getProjectIconDef(project);
  }

  private toDateStr(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}
